import React, { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import Modal from "./Modal";

/**
 * Round-33 — Edit Order (header + lines).
 *
 * Everything on the order becomes editable while status === "open":
 *   • Customer (with product-link validation on the backend)
 *   • Customer PO, PO date, ETA
 *   • Ship-To address (from customer.addresses)
 *   • Shipping method (CPU / LTL / Shipping)
 *   • Ordered-by contact (from customer.contacts)
 *   • Blind shipment
 *   • Production Type (editable even AFTER open — matches existing rule)
 *   • Notes
 *   • Per-line: qty, price, description override, product swap
 *
 * Once the order leaves 'open' status only Production Type stays editable
 * — the backend enforces the same rule and returns 400 otherwise.
 */
export default function EditOrderModal({ order, onClose, onSaved }) {
    const isOpen = order?.status === "open";
    const [savingHeader, setSavingHeader] = useState(false);
    const [savingLineId, setSavingLineId] = useState(null);
    const [error, setError] = useState("");
    const [customers, setCustomers] = useState([]);
    const [customer, setCustomer] = useState(null); // full doc with contacts/addresses
    const [products, setProducts] = useState([]);
    const [header, setHeader] = useState(() => ({
        customer_id: order.customer_id,
        customer_po: order.customer_po || "",
        po_date: order.po_date || "",
        eta: order.eta || "",
        blind_shipment: !!order.blind_shipment,
        notes: order.notes || "",
        production_type: order.production_type || "in_house",
        ship_to_address_id: order.ship_to_snapshot?.address_id || "",
        shipping_method: order.shipping_method || "",
        ordered_by_contact_id: order.ordered_by_snapshot?.contact_id || "",
    }));

    // Load customer list (compact) once
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/customers", { params: { limit: 500 } });
                if (alive) setCustomers(r.data);
            } catch (e) { console.error(e); }
        })();
        return () => { alive = false; };
    }, []);

    // Load full customer doc (contacts, addresses) whenever the selected id changes
    useEffect(() => {
        let alive = true;
        if (!header.customer_id) return;
        (async () => {
            try {
                const [c, p] = await Promise.all([
                    api.get(`/customers/${header.customer_id}`),
                    api.get("/products", {
                        params: { customer_id: header.customer_id, active: true, limit: 500 },
                    }),
                ]);
                if (!alive) return;
                setCustomer(c.data);
                setProducts(p.data);
            } catch (e) {
                if (alive) setError(formatApiError(e?.response?.data?.detail) || e.message);
            }
        })();
        return () => { alive = false; };
    }, [header.customer_id]);

    const addresses = customer?.addresses || [];
    const contacts = customer?.contacts || [];

    async function saveHeader() {
        setSavingHeader(true);
        setError("");
        try {
            const payload = { ...header };
            // Only send changed fields to reduce audit noise
            const diff = {};
            for (const k of Object.keys(payload)) {
                const orig = k === "ship_to_address_id"
                    ? (order.ship_to_snapshot?.address_id || "")
                    : k === "ordered_by_contact_id"
                    ? (order.ordered_by_snapshot?.contact_id || "")
                    : (order[k] ?? "");
                if (payload[k] !== orig) diff[k] = payload[k] === "" ? null : payload[k];
            }
            if (Object.keys(diff).length === 0) {
                onClose();
                return;
            }
            const r = await api.patch(`/orders/${order.id}`, diff);
            onSaved?.(r.data);
            onClose();
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSavingHeader(false);
        }
    }

    async function saveLine(line, patch) {
        setSavingLineId(line.id);
        setError("");
        try {
            const r = await api.patch(
                `/orders/${order.id}/lines/${line.id}`,
                patch,
            );
            onSaved?.({ ...order, lines: order.lines.map((l) => l.id === line.id ? r.data : l) });
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSavingLineId(null);
        }
    }

    return (
        <Modal
            title={`Edit Order — ${order.internal_number}`}
            size="xl"
            onClose={onClose}
        >
            <div data-testid="edit-order-modal" className="space-y-4">
                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}
                {!isOpen && (
                    <div className="text-xs border border-amber-200 bg-amber-50 text-amber-900 rounded-sm px-3 py-2">
                        Order is not open — only Production Type is editable. All
                        other header fields will be rejected by the server.
                    </div>
                )}

                {/* ------------------------------------------------- Header */}
                <section className="border rounded p-3 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700">Order Header</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Customer">
                            <select
                                className="odr-input w-full"
                                value={header.customer_id}
                                onChange={(e) => setHeader({ ...header, customer_id: e.target.value, ship_to_address_id: "", ordered_by_contact_id: "" })}
                                disabled={!isOpen}
                                data-testid="edit-order-customer"
                            >
                                {customers.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Customer PO">
                            <input
                                className="odr-input w-full"
                                value={header.customer_po}
                                onChange={(e) => setHeader({ ...header, customer_po: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-po"
                            />
                        </Field>
                        <Field label="PO Date">
                            <input
                                type="date"
                                className="odr-input w-full"
                                value={header.po_date}
                                onChange={(e) => setHeader({ ...header, po_date: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-po-date"
                            />
                        </Field>
                        <Field label="ETA">
                            <input
                                type="date"
                                className="odr-input w-full"
                                value={header.eta || ""}
                                onChange={(e) => setHeader({ ...header, eta: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-eta"
                            />
                        </Field>
                        <Field label="Ship-To Address">
                            <select
                                className="odr-input w-full"
                                value={header.ship_to_address_id || ""}
                                onChange={(e) => setHeader({ ...header, ship_to_address_id: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-ship-to"
                            >
                                <option value="">— Customer default —</option>
                                {addresses.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.label || "Address"} — {a.address1}, {a.city} {a.state}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Shipping Method">
                            <select
                                className="odr-input w-full"
                                value={header.shipping_method || ""}
                                onChange={(e) => setHeader({ ...header, shipping_method: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-shipping-method"
                            >
                                <option value="">— None —</option>
                                <option value="CPU">CPU (Customer Pick Up)</option>
                                <option value="LTL">LTL (Less-than-truckload)</option>
                                <option value="Shipping">Shipping</option>
                            </select>
                        </Field>
                        <Field label="Ordered By">
                            <select
                                className="odr-input w-full"
                                value={header.ordered_by_contact_id || ""}
                                onChange={(e) => setHeader({ ...header, ordered_by_contact_id: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-ordered-by"
                            >
                                <option value="">— None —</option>
                                {contacts.map((ct) => (
                                    <option key={ct.id} value={ct.id}>
                                        {ct.full_name}{ct.email ? ` (${ct.email})` : ""}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Production Type">
                            <select
                                className="odr-input w-full"
                                value={header.production_type}
                                onChange={(e) => setHeader({ ...header, production_type: e.target.value })}
                                data-testid="edit-order-production-type"
                            >
                                <option value="in_house">In-House</option>
                                <option value="farm_out">Farm-Out</option>
                            </select>
                        </Field>
                        <Field label="Blind Shipment">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={!!header.blind_shipment}
                                    onChange={(e) => setHeader({ ...header, blind_shipment: e.target.checked })}
                                    disabled={!isOpen}
                                    data-testid="edit-order-blind"
                                />
                                Blind ship (hide our identity)
                            </label>
                        </Field>
                        <Field label="Notes" full>
                            <textarea
                                className="odr-input w-full"
                                rows={2}
                                value={header.notes}
                                onChange={(e) => setHeader({ ...header, notes: e.target.value })}
                                disabled={!isOpen}
                                data-testid="edit-order-notes"
                            />
                        </Field>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button
                            className="odr-btn text-sm px-3 py-1 border border-gray-300 rounded-sm"
                            onClick={onClose}
                            data-testid="edit-order-cancel"
                        >
                            Close
                        </button>
                        <button
                            onClick={saveHeader}
                            disabled={savingHeader}
                            className="odr-btn inline-flex items-center gap-1 text-sm px-3 py-1 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white font-semibold disabled:opacity-50"
                            data-testid="edit-order-save-header"
                        >
                            <Save className="w-3.5 h-3.5" /> {savingHeader ? "Saving…" : "Save Header"}
                        </button>
                    </div>
                </section>

                {/* ------------------------------------------------- Lines */}
                <section className="border rounded p-3 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-700">Order Lines</h3>
                    <div className="overflow-x-auto">
                        <table className="odr-table w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-left">#</th>
                                    <th className="text-left">Product</th>
                                    <th className="text-left">Description Override</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Price</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {order.lines.filter((l) => l.active).map((l) => (
                                    <LineRow
                                        key={l.id}
                                        line={l}
                                        products={products}
                                        disabled={!isOpen || savingLineId === l.id}
                                        saving={savingLineId === l.id}
                                        onSave={(patch) => saveLine(l, patch)}
                                    />
                                ))}
                                {order.lines.filter((l) => l.active).length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-xs text-gray-500 py-3">No active lines.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </Modal>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wide mb-1">{label}</label>
            {children}
        </div>
    );
}

function LineRow({ line, products, disabled, saving, onSave }) {
    const [qty, setQty] = useState(line.qty_ordered);
    const [price, setPrice] = useState(line.price);
    const [desc, setDesc] = useState(line.description_override || "");
    const [productId, setProductId] = useState(line.product_id);

    const changed = useMemo(() => (
        qty !== line.qty_ordered
        || String(price) !== String(line.price)
        || (desc || "") !== (line.description_override || "")
        || productId !== line.product_id
    ), [qty, price, desc, productId, line]);

    function submit() {
        const patch = {};
        if (qty !== line.qty_ordered) patch.qty_ordered = Number(qty);
        if (String(price) !== String(line.price)) patch.price = String(price);
        if ((desc || "") !== (line.description_override || "")) patch.description_override = desc;
        if (productId !== line.product_id) patch.product_id = productId;
        onSave(patch);
    }

    return (
        <tr data-testid={`edit-line-row-${line.id}`}>
            <td className="odr-mono text-xs">{line.line_no}</td>
            <td>
                <select
                    className="odr-input w-full text-xs"
                    value={productId}
                    disabled={disabled || (line.produced_qty > 0)}
                    title={line.produced_qty > 0 ? "Product swap disabled — production already recorded" : ""}
                    onChange={(e) => setProductId(e.target.value)}
                    data-testid={`edit-line-product-${line.id}`}
                >
                    {[...products, ...(products.some((p) => p.id === line.product_id) ? [] : [{
                        id: line.product_id,
                        name: line.product_snapshot?.name || "(current product)",
                    }])].map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </td>
            <td>
                <input
                    className="odr-input w-full text-xs"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder={line.product_snapshot?.description || "—"}
                    disabled={disabled}
                    data-testid={`edit-line-desc-${line.id}`}
                />
            </td>
            <td className="text-right">
                <input
                    type="number"
                    min={1}
                    className="odr-input w-24 text-right text-xs"
                    value={qty}
                    onChange={(e) => setQty(Number(e.target.value))}
                    disabled={disabled}
                    data-testid={`edit-line-qty-${line.id}`}
                />
            </td>
            <td className="text-right">
                <input
                    type="text"
                    className="odr-input w-24 text-right text-xs"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    disabled={disabled}
                    data-testid={`edit-line-price-${line.id}`}
                />
            </td>
            <td className="text-right">
                <button
                    onClick={submit}
                    disabled={disabled || !changed}
                    className="odr-btn inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm border border-red-700 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    data-testid={`edit-line-save-${line.id}`}
                >
                    <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                </button>
            </td>
        </tr>
    );
}
